import './PostCard.css';

import React, { useEffect, useState } from 'react';
import { fullImagePath, toLocaleDateTime } from '../../../b2b-common/helpers';
import MoreButton from '../../../b2b-common/MoreButton';
import { ContentCopy, DeleteOutline, Edit, MoreVert, Public, PublicOff, ReportRounded } from '@mui/icons-material';
import ConfirmationDialog from '../../../b2b-common/ConfirmationDialog';
import { deleteInstance } from '../../../../services/b2b-social-apiServices';
import { useLoginContext } from '../../../../App';
import { showNotification } from '../../../b2b-common/Notification';
import { useContentContext } from '../../../layout/Layout';
import EditPostModal from '../../forms/EditPostModal';
import FieldText from '../../../b2b-common/FieldText';
import Counter from '../../../b2b-common/Counter';
import IconWithTooltip from '../../../b2b-common/IconWithTooltip';
import ViewPostModal from '../../forms/ViewPostModal';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';
import { isEqual } from 'lodash';
import useObfuscatePostId from '../../hooks/useObfuscatePostId';

const PostCard = ({ post, template, onRefresh = null }) => {
    const { t } = useB2BTranslation('b2b-social');
    const { setNotification } = useContentContext();
    const { loginData } = useLoginContext();

    const [currentTemplate, setCurrentTemplate] = useState(template);
    const [currentPost, setCurrentPost] = useState(null);
    const obfuscatedPostId = useObfuscatePostId(currentPost?.socialPostInstanceId, 435003);
    const [showEditPostModal, setShowEditPostModal] = useState(false);
    const [showViewPostModal, setShowViewPostModal] = useState(false);
    const [cardHovered, setCardHovered] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    useEffect(() => {
        // console.log('PostCard - post.socialPostInstancePayload.payloadSetup.selectedPlanAreaDTOs:', post?.socialPostInstancePayload?.payloadSetup?.selectedPlanAreaDTOs);
        if (!isEqual(post, currentPost)) {
            setCurrentPost(post);
        }
    }, [post]);

    if (!currentPost) {
        return null;
    }

    const handleEditPost = (event) => {
        event.stopPropagation();
        setShowEditPostModal(true);
    }

    const handleUpdatedPost = (post) => {
        if (!isEqual(post, currentPost)) {
            setCurrentPost(post);
            if (onRefresh) {
                onRefresh();
            }
        }
    }

    const handleDeletePostConfirmation = async () => {
        try {
            await deleteInstance(loginData.accessToken, currentPost.socialPostInstanceId);
            showNotification(setNotification, t('Post deleted successfully'), 'success', 3000);
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error deleting post: ' + error.message);
            showNotification(setNotification, t('Error deleting post: ') + error.message, 'error', 5000);
        } finally {
            setIsConfirmationOpen(false);
        }
    }

    const handleClickOnCard = (event) => {
        event.stopPropagation();
        if (!showEditPostModal)
            setShowViewPostModal(true);
    }

    const handleCloseViewPostModal = (event) => {
        event.stopPropagation();
        // console.log('handleCloseViewPostModal called, showViewPostModal:', showViewPostModal);
        if (showViewPostModal) {
            // console.log('Setting showViewPostModal to false');
            setShowViewPostModal(false);
        }
    }

    const handleCopyLink = (event) => {
        event.stopPropagation();
        const urlToCopy = `${window.location.origin}/pub/social/post/${obfuscatedPostId}`;
        navigator.clipboard.writeText(urlToCopy)
            .then(() => {
                console.log('URL copied to clipboard:', urlToCopy);
                showNotification(setNotification, t('Link copied to clipboard'), 'success', 3000);
            })
            .catch((error) => {
                console.error('Failed to copy URL:', error);
                showNotification(setNotification, t('Failed to copy link'), 'error', 5000);
            });
    }

    const postIsExpired = new Date(currentPost.socialPostInstanceExpirationDate) < new Date();
    const postIsEnabled = currentPost.socialPostInstanceEnabled;
    const postIsPublic = currentPost.socialPostInstancePrivacy === 'PUBLIC';
    const postIsNotAvailable = postIsExpired || !postIsEnabled;

    return (
        <div className={(`post-card-container ${postIsNotAvailable ? 'post-not-available' : ''}`)}
            onMouseEnter={() => setCardHovered(true)}
            onMouseLeave={() => setCardHovered(false)}
            onClick={(event) => handleClickOnCard(event)}
        >
            {postIsNotAvailable && (
                <IconWithTooltip IconComponent={ReportRounded} tooltip={t('Post is not publicly available. Either is expired or disabled.')} className={'post-not-available-icon'} />
            )}
            {cardHovered && (
                <MoreButton icon={<MoreVert />}
                    buttons={[
                        {
                            label: t('Copy link to post'),
                            icon: <ContentCopy />,
                            rolesAllowed: ['ADMIN'],
                            onClick: (event) => handleCopyLink(event),
                        },
                        {
                            label: t('Edit'),
                            icon: <Edit />,
                            rolesAllowed: ['ADMIN'],
                            onClick: (event) => handleEditPost(event),
                        },
                        {
                            label: t('Delete'),
                            icon: <DeleteOutline />,
                            rolesAllowed: ['ADMIN'],
                            onClick: () => setIsConfirmationOpen(true)
                        },
                    ]}
                    dropPositionAlign='right'
                    rolesAllowed={['ADMIN', 'EDITOR', 'USER']}
                    className={'jb01-more-button'}
                />
            )}
            <div className="post-card-template-image">
                <img src={fullImagePath(currentTemplate.templateImage)} alt={currentTemplate.socialPostTemplateName} />
            </div>
            <div className="post-card-content">
                <div className="post-card-name-and-description">
                    <div className={`post-card-name ${postIsEnabled ? 'post-enabled' : 'post-disabled'}`}>{currentPost.socialPostInstanceName}</div>
                    <div className="post-card-description">{currentPost.socialPostInstanceDescription}</div>
                </div>
                <div className="post-card-dates-and-status">
                    <div className="post-card-dates">
                        <FieldText label={t('Locale: ')} text={currentPost.socialPostInstanceLocale.toUpperCase()} className={'post-card-date theme-fieldtex-horizontal-01'} />
                        <FieldText label={t('Created: ')} text={toLocaleDateTime(currentPost.creationDate, false)} className={'post-card-date theme-fieldtex-horizontal-01'} />
                        <FieldText label={t('Updated: ')} text={toLocaleDateTime(currentPost.lastModified, false)} className={'post-card-date theme-fieldtex-horizontal-01'} />
                        <FieldText label={t('Expiration: ')} text={toLocaleDateTime(currentPost.socialPostInstanceExpirationDate, false)} className={`post-card-date ${postIsExpired ? 'post-expired' : ''} theme-fieldtex-horizontal-01`} />
                    </div>
                    <div className="post-card-status">

                        {postIsPublic ? (<IconWithTooltip IconComponent={Public} tooltip={t('Public')} className={'post-card-status-privacy'} />)
                            : (<IconWithTooltip IconComponent={PublicOff} tooltip={t('Private')} className={'post-card-status-privacy'} />)
                        }

                        <Counter label={t('Visits')} count={currentPost.socialPostInstanceVisits} minDigits={2} className={'post-card-counter'} />
                    </div>
                </div>
            </div>
            <ConfirmationDialog
                title={t('Post Delete Confirmation Request')}
                mainText={t('Are you sure you want to delete the selected post?')}
                open={isConfirmationOpen}
                onClose={() => setIsConfirmationOpen(false)}
                onConfirm={handleDeletePostConfirmation}
            />
            {showEditPostModal && (
                <EditPostModal
                    operation='UPDATE'
                    post={currentPost}
                    template={currentTemplate}
                    open={showEditPostModal}
                    onClose={() => setShowEditPostModal(false)}
                    onSavedPost={(post) => handleUpdatedPost(post)}
                />
            )}
            <ViewPostModal
                post={currentPost}
                open={showViewPostModal}
                onClose={handleCloseViewPostModal}
            />
        </div >
    );
}

export default PostCard;